import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheck, FiDollarSign, FiCalendar, FiCreditCard, FiArrowRight } from 'react-icons/fi';
import { format } from 'date-fns';
import api from '../api';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const verifyingRef = useRef(false); // 중복 호출 방지

  useEffect(() => {
    // 이미 검증 중이면 중복 호출 방지
    if (verifyingRef.current) return;

    verifyingRef.current = true;
    confirmPayment();
  }, []);

  const confirmPayment = async () => {
    try {
      const imp_uid = searchParams.get('imp_uid');
      const merchant_uid = searchParams.get('merchant_uid');
      const amount = parseInt(searchParams.get('amount'));

      if (!imp_uid || !merchant_uid || !amount) {
        throw new Error('결제 정보가 올바르지 않습니다.');
      }

      // 서버에서 아임포트 결제 검증
      const res = await api.post('/api/payments/verify', {
        imp_uid,
        merchant_uid,
      });

      setPaymentResult(res.data);
    } catch (error) {
      console.error('Payment verification error:', error);
      setError(error.response?.data?.error || error.message || '결제 검증에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      '카드': '신용/체크카드',
      '가상계좌': '가상계좌',
      '간편결제': '간편결제',
      '계좌이체': '계좌이체',
    };
    return labels[method] || method;
  };

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <h3>결제를 처리하고 있습니다...</h3>
          <p>잠시만 기다려주세요</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result-page">
        <div className="result-container error">
          <div className="result-icon error">
            <span>✕</span>
          </div>
          <h2>결제 승인 실패</h2>
          <p className="error-message">{error}</p>
          <div className="actions">
            <button className="btn-secondary" onClick={() => navigate('/points/charge')}>
              다시 시도
            </button>
            <button className="btn-primary" onClick={() => navigate('/points')}>
              포인트 관리로 이동
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { payment, processingResult } = paymentResult;

  return (
    <div className="payment-result-page">
      <div className="result-container success">
        {/* 성공 아이콘 */}
        <div className="result-icon success">
          <FiCheck size={64} />
        </div>

        {/* 제목 */}
        <h2>포인트 충전 완료!</h2>
        <p className="success-message">
          {payment.amount.toLocaleString()}P가 충전되었습니다
        </p>

        {/* 결제 정보 */}
        <div className="payment-details-card">
          <div className="detail-row">
            <span className="label">
              <FiDollarSign />
              충전 금액
            </span>
            <span className="value">{payment.amount.toLocaleString()}원</span>
          </div>

          <div className="detail-row">
            <span className="label">
              <FiDollarSign />
              충전 포인트
            </span>
            <span className="value highlight">{payment.amount.toLocaleString()}P</span>
          </div>

          <div className="detail-row">
            <span className="label">
              <FiCreditCard />
              결제 수단
            </span>
            <span className="value">
              {getPaymentMethodLabel(
                payment.receipt?.card?.company ||
                payment.receipt?.virtualAccount?.bank ||
                payment.receipt?.easyPay?.provider ||
                '결제완료'
              )}
            </span>
          </div>

          <div className="detail-row">
            <span className="label">
              <FiCalendar />
              결제 일시
            </span>
            <span className="value">
              {format(new Date(payment.approvedAt), 'yyyy년 MM월 dd일 HH:mm')}
            </span>
          </div>

          <div className="detail-row">
            <span className="label">주문번호</span>
            <span className="value small">{payment.orderId}</span>
          </div>
        </div>

        {/* 현재 잔액 */}
        {processingResult?.point && (
          <div className="balance-card">
            <h4>현재 포인트 잔액</h4>
            <div className="balance-value">{processingResult.point.balance.toLocaleString()}P</div>
          </div>
        )}

        {/* 영수증 링크 */}
        {payment.receiptUrl && (
          <div className="receipt-link">
            <a href={payment.receiptUrl} target="_blank" rel="noopener noreferrer">
              영수증 확인하기 <FiArrowRight />
            </a>
          </div>
        )}

        {/* 액션 버튼 */}
        <div className="actions">
          <button className="btn-secondary" onClick={() => navigate('/points/history')}>
            충전 내역 보기
          </button>
          <button className="btn-primary" onClick={() => navigate('/points')}>
            포인트 관리로 이동
          </button>
        </div>
      </div>
    </div>
  );
}
